import type { FetchFn, BeckyTopology } from '@wix/thunderbolt-becky-types'
import { getTopologyUrl, VIEWER_APPS_FALLBACK_URL, VIEWER_APPS_URL_KEY } from '../topology/topologyUtils'
import type { BuilderAppVersions } from './builderComponentApps'
import _ from 'lodash'

export type BuilderComponentsHierarchy = { [compType: string]: string[] }

export type AppDependency = {
	appId: string
	componentDependencies: {
		type: string
		dependencies: string[]
	}
}

const APP_DEPENDENCY_RESOLVER_PATH = '/app-dependency-resolver/v1/apps/dependencies'

export const getBuilderComponentHierarchy = async (
	_parsedBuilderAppVersions: BuilderAppVersions,
	fetchFn: FetchFn,
	onError: (error: Error) => void,
	beckyTopology?: BeckyTopology,
): Promise<BuilderComponentsHierarchy> => {
	try {
		// sort the query params to avoid cache miss becuase of different order of the app def ids but with the same versions
		const sortedParsedBuilderAppVersions = _parsedBuilderAppVersions.sort(
			(a, b) => a.appId.localeCompare(b.appId)
		)
		const queryParam = sortedParsedBuilderAppVersions
			.map(({ appId, version }) => {
				if (appId === 'b976560c-3122-4351-878f-453f337b7245') {
					return `appKeys=${appId}:dev`
				}
				return `appKeys=${appId}:${version}`
			})
			.join('&')
		if (queryParam.length === 0) {
			return {}
		}

		const viewerAppsBaseUrl = getTopologyUrl(beckyTopology, VIEWER_APPS_URL_KEY, VIEWER_APPS_FALLBACK_URL)
		const response = await fetchFn(
			`${viewerAppsBaseUrl}${APP_DEPENDENCY_RESOLVER_PATH}?${queryParam}`,
			{
				method: 'GET',
			}
		)
		const { appsDependencies }: { appsDependencies: AppDependency[] } =
			await response.json()
		if (!appsDependencies) {
			return {}
		}
		const builderComponentHierarchy = _(appsDependencies)
			.flatMap((a) => a.componentDependencies || [])
			.groupBy('type') // merge same component across apps/versions
			.mapValues((items) => _.uniq(_.flatMap(items, 'dependencies')))
			.value()
		return builderComponentHierarchy
	} catch (error) {
		onError(error)
		return {}
	}
}

export const getCompTypeToAllDescendants = (
	compTypeToDirectChildren: BuilderComponentsHierarchy
): BuilderComponentsHierarchy => {
	const result: BuilderComponentsHierarchy = {}
	const cache: Record<string, string[]> = {}

	const calculateDescendants = (
		compType: string,
		currentPath: string[] = []
	): string[] => {
		if (cache[compType]) {
			return cache[compType]
		}

		if (currentPath.includes(compType)) {
			return []
		}

		const directChildren = (compTypeToDirectChildren[compType] || []).filter((child) => child !== compType)
		if (directChildren.length === 0) {
			cache[compType] = []
			return []
		}

		const allDescendants = new Set<string>()
		const newPath = [...currentPath, compType]

		// Add all descendants of each child
		directChildren.forEach((child) => {
			allDescendants.add(child)
			const childDescendants = calculateDescendants(child, newPath)
			childDescendants.forEach((descendant) =>
				allDescendants.add(descendant)
			)
		})

		const descendants = Array.from(allDescendants)
		cache[compType] = descendants
		return descendants
	}

	// Calculate descendants for all components
	Object.keys(compTypeToDirectChildren).forEach((compType) => {
		result[compType] = calculateDescendants(compType)
	})

	// Handle cycles: find all strongly connected components
	const findCycleMembers = (startComponent: string): string[] => {
		const cycleMembers = new Set<string>()
		const toCheck = [startComponent]

		while (toCheck.length > 0) {
			const current = toCheck.pop()!
			if (cycleMembers.has(current)) continue

			// If current can reach startComponent and startComponent can reach current, they're in the same cycle
			if (
				result[current]?.includes(startComponent) &&
				result[startComponent]?.includes(current)
			) {
				cycleMembers.add(current)
				// Add all descendants of this cycle member to check
				result[current]?.forEach((descendant) => {
					if (!cycleMembers.has(descendant)) {
						toCheck.push(descendant)
					}
				})
			}
		}

		return Array.from(cycleMembers)
	}

	// For each component, check if it's in a cycle and update all cycle members
	const processedCycles = new Set<string>()
	Object.keys(result).forEach((compType) => {
		if (processedCycles.has(compType)) {
			return
		}

		const cycleMembers = findCycleMembers(compType)
		if (cycleMembers.length > 1) {
			// All cycle members should contain all other cycle members as descendants
			cycleMembers.forEach((member) => {
				const currentDescendants = new Set(result[member])
				cycleMembers.forEach((otherMember) => {
					if (member !== otherMember) {
						currentDescendants.add(otherMember)
					}
				})
				result[member] = Array.from(currentDescendants)
				processedCycles.add(member)
			})
		}
	})

	return result
}
